import { gql, useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import Tag from "components/base/Tag";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import { FETCH_TAGS, REPLACE_OBJECT_ALL_TAGS } from "./graphql";

const FETCH_CUSTOMER_TAGS = gql`
  query REFETCH_($id: ID!) {
    tags: taggingObjectTags(
      contentTypeName: "customer"
      contentTypeAppLabel: "customer"
      objectId: $id
    ) {
      id
      value
      label
    }
  }
`;

const ObjectTagsManager = ({
  initialTags,
  contentTypeAppLabel,
  contentTypeName,
  objectId,
  hide,
}) => {
  const { loading, error, data } = useQuery(FETCH_TAGS, {
    fetchPolicy: "network-only",
  });
  const [replaceObjectAllTags, replaceObjectAllTagsRes] = useMutation(
    REPLACE_OBJECT_ALL_TAGS,
    {
      refetchQueries: [
        {
          query: FETCH_CUSTOMER_TAGS,
          variables: { id: objectId },
        },
      ],
    },
  );

  const [q, setQ] = useState("");
  const [tags, setTags] = useState(initialTags || []);

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const options = data.taggingTags.map((i) => ({
    id: i.id,
    label: `${i.category.name}: ${i.value}`,
  }));

  const filteredOptions = options
    .filter((option) => !tags.some((tag) => tag.id === option.id))
    .filter((option) => option.label.toLowerCase().includes(q.toLowerCase()));

  const canSave =
    Number.isInteger(Number(objectId)) &&
    String(contentTypeName || "").trim() !== "";

  function saveHandler() {
    if (!canSave) return;

    replaceObjectAllTags({
      variables: {
        tagIds: tags.map((i) => i.id),
        objectId: Number(objectId),
        contentTypeName: contentTypeName || null,
        contentTypeAppLabel: contentTypeAppLabel || null,
        contentTypeId: null,
      },
    })
      .then(() => {
        Alert("success", "Tags saved.");
        if (hide) hide();
      })
      .catch((error) => {
        window.alert(parseError(error));
      });
  }

  return (
    <div className="text-sm">
      <div className="card">
        <h5>Selected Tags</h5>
        <div className="mt-2 gap-2 flex flex-wrap text-xs">
          {tags.length === 0 ? (
            <div className="text-gray-500">No tags selected</div>
          ) : (
            tags.map((tag) => (
              <div
                key={tag.id}
                className="flex items-center gap-2 border px-3 py-1 rounded-full dark:border-gray-600"
              >
                <div className="font-semibold">{tag.label}</div>
                <Button
                  danger
                  onClick={() => {
                    setTags(tags.filter((t) => t.id !== tag.id));
                  }}
                >
                  &times;
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-6 card">
        <h5>Choose Tags Below</h5>
        <SearchBar
          placeholder="Search tags..."
          className="mt-2"
          value={q}
          onChange={setQ}
        />
        <div className="max-h-56 flex flex-wrap gap-2 overflow-auto mt-3">
          {filteredOptions.map((option) => (
            <Tag
              key={option.id}
              onClick={() => {
                setTags([...tags, option]);
              }}
              value={option.label}
            />
          ))}
        </div>
      </div>

      <div className="mt-6">
        <Button
          primary
          onClick={saveHandler}
          loading={replaceObjectAllTagsRes.loading}
          disabled={!canSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default ObjectTagsManager;
